import { useEffect, useRef, useState } from 'react'
import { FetchUserFilesFnType } from '../types'
import { DataFile } from 'client/store/files/filesReducer'
import axios, { CancelTokenSource } from 'axios'
import { useApiWrapperWithValidation } from 'src/editor/api/hooks/use-api-wrapper-with-validation'

interface UseFileLibraryProps {
  files: Record<string, DataFile>
  fileType?: string
  fetchData: FetchUserFilesFnType
  firstFetchLimit?: number
  additionalFetchLimit?: number
}

export const useFileLibrary = ({
  files,
  fileType,
  fetchData,
  firstFetchLimit = 30,
  additionalFetchLimit = 10,
}: UseFileLibraryProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [cancelTokenSourceState, setCancelTokenSourceState] =
    useState<null | CancelTokenSource>(null)
  const [isAllFilesFetched, setIsAllFilesFetched] = useState(false)

  const fileListRef = useRef<HTMLDivElement>(null)
  const previousScrollTopRef = useRef(0)
  const countForLoadingRef = useRef(30)
  const filesKeysRef = useRef(Object.keys(files))

  const { fetcher: fetchDataWithValidation, isFetching } =
    useApiWrapperWithValidation({
      requestFunction: fetchData,
    })

  const getLastId = () => filesKeysRef.current[0]

  const normalizeCountForLoading = (
    value: number,
    step = additionalFetchLimit,
    min = firstFetchLimit,
  ): number => {
    const rounded = Math.ceil(value / step) * step
    return Math.max(min, rounded)
  }

  const onScroll = async () => {
    const fileList = fileListRef.current
    if (isFetching || isAllFilesFetched || !fileList) {
      return
    }
    const currentScrollTop = fileList.scrollTop
    const isScrollingDown = currentScrollTop > previousScrollTopRef.current
    previousScrollTopRef.current = currentScrollTop
    const isAtBottom =
      currentScrollTop + fileList.clientHeight >= fileList.scrollHeight - 50

    if (isScrollingDown && isAtBottom) {
      const fetchedFiles = await fetchDataWithValidation(
        fileType,
        getLastId(),
        additionalFetchLimit,
        searchValue,
      )
      if (Array.isArray(fetchedFiles) && fetchedFiles.length === 0) {
        setIsAllFilesFetched(true)
      }
    }
  }

  const onSearchChange = async (newSearchValue: string) => {
    if (searchValue !== newSearchValue) {
      setIsAllFilesFetched(false)
      setSearchValue(newSearchValue)
    }
    const cancelTokenSource = axios.CancelToken.source()
    if (cancelTokenSourceState) {
      cancelTokenSourceState.cancel()
    }
    setCancelTokenSourceState(cancelTokenSource)
    const fetchedFiles = await fetchDataWithValidation(
      fileType,
      null,
      firstFetchLimit,
      newSearchValue,
      cancelTokenSource.token,
    )

    if (Array.isArray(fetchedFiles) && fetchedFiles.length === 0) {
      setIsAllFilesFetched(true)
    }
  }

  useEffect(() => {
    const fileList = fileListRef.current
    fileList?.addEventListener('scroll', onScroll)
    return () => {
      fileList?.removeEventListener('scroll', onScroll)
    }
  }, [isAllFilesFetched, isFetching, searchValue])

  useEffect(() => {
    filesKeysRef.current = Object.keys(files)
    countForLoadingRef.current = normalizeCountForLoading(
      Object.keys(files).length,
    )
  }, [files])

  return {
    isFetching,
    onSearchChange,
    fileListRef,
    searchValue,
  }
}
